import { defineStore } from "pinia";
import TokenService from "@/services/token.service";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface StoredChatData {
  conversationId: string;
  messages: ChatMessage[];
  lastActivityTimestamp: number;
}

const STORAGE_KEY = "delphin_chatbot_session";
const TTL_MS = 24 * 60 * 60 * 1000; // 24 Saat (ms)
const MAX_SAVED_MESSAGES = 100;

function generateConversationId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof (crypto as any).randomUUID === "function"
  ) {
    return (crypto as any).randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getDefaultApiBaseUrl(): string {
  let url = "https://www.delphinpiraye.com/api";
  if (typeof window !== "undefined") {
    const href = window.location.href;
    if (href.startsWith("http://localhost:4200")) {
      url = "http://localhost:5000/api";
    } else if (href.startsWith("https://test.delphinpiraye.com")) {
      url = "https://test.delphinpiraye.com/api";
    }
  }
  return url;
}

function loadInitialState() {
  try {
    if (typeof window !== "undefined") {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data: StoredChatData = JSON.parse(raw);
        const now = Date.now();
        // 24 saatlik süre dolmamışsa geçmişi yükle
        if (
          data.lastActivityTimestamp &&
          now - data.lastActivityTimestamp < TTL_MS
        ) {
          return {
            conversationId: data.conversationId || generateConversationId(),
            messages: Array.isArray(data.messages) ? data.messages : [],
            lastActivityTimestamp: data.lastActivityTimestamp,
          };
        } else {
          // TTL aşılmışsa eski oturumu temizle
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    }
  } catch (e) {
    console.warn("Chat geçmişi yüklenirken hata oluştu:", e);
  }

  return {
    conversationId: generateConversationId(),
    messages: [] as ChatMessage[],
    lastActivityTimestamp: Date.now(),
  };
}

export const useChatStore = defineStore("chat", {
  state: () => {
    const initial = loadInitialState();
    return {
      conversationId: initial.conversationId,
      messages: initial.messages,
      lastActivityTimestamp: initial.lastActivityTimestamp,
      isOpen: false,
      isLoading: false,
      isStreaming: false,
    };
  },

  actions: {
    persistState() {
      try {
        if (typeof window !== "undefined") {
          const dataToSave: StoredChatData = {
            conversationId: this.conversationId,
            messages: this.messages.slice(-MAX_SAVED_MESSAGES),
            lastActivityTimestamp: this.lastActivityTimestamp,
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
        }
      } catch (e) {
        console.warn("Chat durumu kaydedilemedi:", e);
      }
    },

    toggleOpen() {
      this.isOpen = !this.isOpen;
    },

    setOpen(value: boolean) {
      this.isOpen = value;
    },

    clearMessages() {
      this.messages = [];
      this.conversationId = generateConversationId();
      this.lastActivityTimestamp = Date.now();
      this.isLoading = false;
      this.isStreaming = false;
      try {
        if (typeof window !== "undefined") {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.warn("Storage temizlenemedi:", e);
      }
    },

    async sendMessage(
      text: string,
      apiUrl?: string,
      callbacks?: {
        onMessageSent?: (msg: string) => void;
        onMessageReceived?: (msg: string) => void;
        onError?: (err: unknown) => void;
        onScrollRequest?: () => void;
      }
    ) {
      const trimmedText = text.trim();
      if (!trimmedText || this.isLoading) return;

      this.messages.push({ role: "user", content: trimmedText });
      this.lastActivityTimestamp = Date.now();
      this.persistState();

      if (callbacks?.onMessageSent) {
        callbacks.onMessageSent(trimmedText);
      }
      if (callbacks?.onScrollRequest) {
        callbacks.onScrollRequest();
      }

      if (!this.conversationId) {
        this.conversationId = generateConversationId();
      }

      const payload = {
        conversationId: this.conversationId,
        message: trimmedText,
      };

      this.isLoading = true;
      this.isStreaming = false;

      // Asistan için boş mesaj alanı ekle
      this.messages.push({ role: "assistant", content: "" });
      const assistantMessageIndex = this.messages.length - 1;

      if (callbacks?.onScrollRequest) {
        callbacks.onScrollRequest();
      }

      const baseUrl = apiUrl || getDefaultApiBaseUrl();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      try {
        const token = TokenService.getLocalAccessToken();
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      } catch (e) {
        console.warn("Token alınamadı:", e);
      }

      try {
        const response = await fetch(`${baseUrl}/chat`, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

        if (response.ok && response.body) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder("utf-8");
          let done = false;
          let buffer = "";

          while (!done) {
            const { value, done: readerDone } = await reader.read();
            done = readerDone;
            if (value) {
              buffer += decoder.decode(value, { stream: true });
              const parts = buffer.split("\n\n");
              buffer = parts.pop() || "";

              for (const part of parts) {
                const lines = part.split("\n");
                for (const line of lines) {
                  if (line.startsWith("data:")) {
                    let dataStr = line.slice(5);
                    if (dataStr.startsWith(" ")) {
                      dataStr = dataStr.slice(1);
                    }

                    if (dataStr === "[DONE]") continue;

                    let token = "";
                    try {
                      const json = JSON.parse(dataStr);
                      if (json.choices?.[0]?.delta?.content) {
                        token = json.choices[0].delta.content;
                      } else if (json.content) {
                        token = json.content;
                      } else if (json.message) {
                        token = json.message;
                      } else if (typeof json === "string") {
                        token = json;
                      }
                    } catch (e) {
                      token = dataStr;
                    }

                    if (typeof token === "string") {
                      token = token.replace(/\\n/g, "\n");
                    }

                    if (token) {
                      if (!this.isStreaming) this.isStreaming = true;
                      this.messages[assistantMessageIndex].content += token;
                      if (callbacks?.onScrollRequest) {
                        callbacks.onScrollRequest();
                      }
                    }
                  }
                }
              }
            }
          }

          if (
            assistantMessageIndex >= 0 &&
            this.messages[assistantMessageIndex]?.content
          ) {
            this.lastActivityTimestamp = Date.now();
            this.persistState();
            if (callbacks?.onMessageReceived) {
              callbacks.onMessageReceived(
                this.messages[assistantMessageIndex].content
              );
            }
          }
        } else {
          this.messages[assistantMessageIndex].content =
            "Sunucu hatası, mesaj gönderilemedi.";
          this.lastActivityTimestamp = Date.now();
          this.persistState();
        }
      } catch (error) {
        console.error("ChatBot error:", error);
        if (assistantMessageIndex >= 0) {
          this.messages[assistantMessageIndex].content = "Ağ hatası oluştu.";
        }
        if (callbacks?.onError) {
          callbacks.onError(error);
        }
        this.lastActivityTimestamp = Date.now();
        this.persistState();
      } finally {
        this.isStreaming = false;
        this.isLoading = false;
        if (callbacks?.onScrollRequest) {
          callbacks.onScrollRequest();
        }
      }
    },
  },
});
